/* eslint-disable no-console */
import Drawflow from "drawflow";

import IWorkflowElement from "@/interfaces/workflow-element.interfaces";
import { IWorkflowStep } from "@/interfaces/workflow-step.interface";
import { IWorkflow } from "@/interfaces/workflow.interface";
import { WorkflowElements } from "@/types/workflow.types";

interface ElementIcons {
  [key: string]: string;
}

const elementIcons: ElementIcons = {
  Begin:
    "<svg><path d='M14.59 7.41 18.17 11H6v2h12.17l-3.59 3.59L16 18l6-6-6-6zM2 6v12h2V6z'></path></svg>",
  Message:
    "<svg><path d='M4 4h16v12H5.17L4 17.17zm0-2c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm2 10h8v2H6zm0-3h12v2H6zm0-3h12v2H6z'></path></svg>",
  Menu: "<svg><path d='M4 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5m0-6c-.83 0-1.5.67-1.5 1.5S3.17 7.5 4 7.5 5.5 6.83 5.5 6 4.83 4.5 4 4.5m0 12c-.83 0-1.5.68-1.5 1.5s.68 1.5 1.5 1.5 1.5-.68 1.5-1.5-.67-1.5-1.5-1.5M7 19h14v-2H7zm0-6h14v-2H7zm0-8v2h14V5z'></path></svg>",
  Process:
    "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' class='feather feather-settings'><circle cx='12' cy='12' r='3'></circle><path d='M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z'></path></svg>",
  Question:
    "<svg><path d='M4 6H2v14c0 1.1.9 2 2 2h14v-2H4zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2m0 14H8V4h12zm-6.49-5.84c.41-.73 1.18-1.16 1.63-1.8.48-.68.21-1.94-1.14-1.94-.88 0-1.32.67-1.5 1.23l-1.37-.57C11.51 5.96 12.52 5 13.99 5c1.23 0 2.08.56 2.51 1.26.37.6.58 1.73.01 2.57-.63.93-1.23 1.21-1.56 1.81-.13.24-.18.4-.18 1.18h-1.52c.01-.41-.06-1.08.26-1.66m-.56 3.79c0-.59.47-1.04 1.05-1.04.59 0 1.04.45 1.04 1.04 0 .58-.44 1.05-1.04 1.05-.58 0-1.05-.47-1.05-1.05'></path></svg>",
  Input:
    "<svg><path d='M3 17.25V21h3.75L17.81 9.94l-3.75-3.75zM5.92 19H5v-.92l9.06-9.06.92.92zM20.71 5.63l-2.34-2.34c-.2-.2-.45-.29-.71-.29s-.51.1-.7.29l-1.83 1.83 3.75 3.75 1.83-1.83c.39-.39.39-1.02 0-1.41'></path></svg>",
};

export function calculateNodePosition(stepNumber: number) {
  const x = Math.floor(stepNumber % 5) * 400;
  const y = Math.floor(stepNumber / 5) * 400;
  return { x, y };
}

export function generateNodeHTML(step: IWorkflowStep, element: IWorkflowElement | null): string {
  if (!element) {
    return "<div><p>Element not provided</p></div>";
  }

  const expectedId = `step-${step.id}`;
  const iconHTML = elementIcons[element.type] || "<svg>...</svg>";

  let contentHTML = step.startMessage ? `<p>${step.startMessage}</p>` : "";

  if (element.id === WorkflowElements.Menu && step.menuOption !== "") {
    try {
      const menuOptions = JSON.parse(step.menuOption);
      const menuOptionsHTML = menuOptions
        .map(
          (option: { Option: number; Description: string }) =>
            `${option.Option} - ${option.Description}`
        )
        .join("<br>");
      contentHTML = menuOptionsHTML;
    } catch (error) {
      contentHTML = "<p>Menu options could not be displayed</p>";
    }
  }

  return `<div id="${expectedId}">
             <div class="title-box">
               <span class="element-icon">${iconHTML}</span> 
               <p>${element.type}</p>
             </div>
             ${contentHTML ? ` <div class="content-box">${contentHTML}</div>` : ""}
           </div>`;
}

export function initializeConnections(step: IWorkflowStep, workflow: IWorkflow, editor: Drawflow) {
  // Conectar com o step referenciado em gotoWorkflowStepName
  if (step.gotoWorkflowStepName) {
    const gotoStep = workflow.workflowSteps.find((s) => s.stepName === step.gotoWorkflowStepName);
    if (gotoStep) {
      connectNodes(step, gotoStep, editor);
    }
  } else {
    // Conectar com o próximo step baseado no stepNumber
    const nextStepNumber = step.stepNumber + 1;
    const nextStep = workflow.workflowSteps.find((s) => s.stepNumber === nextStepNumber);

    if (nextStep) {
      connectNodes(step, nextStep, editor);
    }
  }

  // Conectar com o step referenciado em exceptionSourceWorkflowStepName
  if (step.exceptionSourceWorkflowStepName) {
    const exceptionStep = workflow.workflowSteps.find(
      (s) => s.stepName === step.exceptionSourceWorkflowStepName
    );
    if (exceptionStep) {
      connectNodes(step, exceptionStep, editor);
    }
  }

}

export function connectNodes(step: IWorkflowStep, targetStep: IWorkflowStep, editor: Drawflow) {
  if (targetStep?.nodeId && step.nodeId) {
    const stepNodeId = Number(step.nodeId);
    const targetStepNodeId = Number(targetStep.nodeId);

    try {
      editor.addConnection(stepNodeId, targetStepNodeId, "output_1", "input_1");
    } catch (error) {
      console.error("Erro ao tentar adicionar conexão:", error);
    }
  }
}
